import { InfiniteData, infiniteQueryOptions } from '@tanstack/react-query'
import { AccountRevisionListModel } from 'src/models/accountRevision.models'
import {
  BudgetRevisionsListParams,
  BudgetRevisionsResponse,
} from 'src/queries/creditCards/budgetRevisions/budgetRevisions.models'
import { getBudgetRevisions } from 'src/queries/creditCards/budgetRevisions/budgetRevisions.network'
import {
  creditCardsQueryKeys,
  REVISIONS_QK,
} from 'src/queries/queries.constants'

export const normalizeBudgetRevisionsListParams = (
  params?: BudgetRevisionsListParams,
): BudgetRevisionsListParams => {
  const normalizedParams: BudgetRevisionsListParams = {}
  if (params?.limit !== undefined) normalizedParams.limit = params.limit
  if (params?.order) normalizedParams.order = params.order
  return normalizedParams
}

// Keys are composed from creditCardsQueryKeys.detail(...) in
// queries.constants.ts (same shape as virtualCardsQueryKeys.revisions for
// virtualcards/{id}/revisions) so that the existing
// invalidateQueries({ queryKey: creditCardsQueryKeys.detail(id) }) call sites
// (App.tsx, PhysicalCardTransactionsPanel, VirtualCardCapabilitiesPanel,
// creditCards.actions.tsx) prefix-invalidate this revision history whenever a
// card mutation invalidates the card itself.
// Shape: ['creditcards', creditCardId, 'revisions', params]
export const budgetRevisionsQueryKeys = {
  list: (
    creditCardId: string | undefined,
    params?: BudgetRevisionsListParams,
  ) =>
    [
      ...creditCardsQueryKeys.detail(creditCardId),
      REVISIONS_QK,
      params ?? {},
    ] as const,
}

/**
 * Flattens infinite pages into the AccountRevisionListModel[] shape that
 * BudgetHistoryTable renders. Shared by the BudgetHistory and AccountHistory
 * pages (pass as `select` at the useInfiniteQuery call site).
 */
export const selectBudgetRevisionListModels = (
  data: InfiniteData<BudgetRevisionsResponse, string | undefined>,
): AccountRevisionListModel[] =>
  data.pages.flatMap((page) =>
    page.revisions.map((revision) => new AccountRevisionListModel(revision)),
  )

export const budgetRevisionsQueries = {
  list: (
    creditCardId: string | undefined,
    params?: BudgetRevisionsListParams,
  ) => {
    const normalizedParams = normalizeBudgetRevisionsListParams(params)

    return infiniteQueryOptions({
      queryKey: budgetRevisionsQueryKeys.list(creditCardId, normalizedParams),
      queryFn: ({ pageParam }) =>
        getBudgetRevisions(creditCardId!, {
          ...normalizedParams,
          ...(pageParam !== undefined ? { nextCursor: pageParam } : {}),
        }),
      enabled: Boolean(creditCardId),
      initialPageParam: undefined as string | undefined,
      // nextCursor === undefined means fully loaded (the Redux page slices
      // derived fullyLoadedRevisions the same way); consumers read !hasNextPage.
      getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
      // Forward-only on purpose: the queryFn maps pageParam to nextCursor, so
      // no getPreviousPageParam is defined (fetchPreviousPage would misroute a
      // previousCursor into nextCursor). The Redux thunk's previousCursor
      // fetch-on-mount existed only to top up a persisted cache with newer
      // revisions; React Query replaces it by refetching page 1+ on remount.
      // No queryCacheOptions()/staleTime override: revision history is mutable
      // server state (every card edit appends a revision), so the app
      // QueryClient defaults (staleTime 0, refetch on remount with a single
      // observer) intentionally replace the Redux behavior of refetching the
      // newest page on every page mount.
    })
  },
}
